/**
 * Cloud access for `saved_sell_plans` <- localStorage `savedSellPlans`
 * (unprefixed key). Frozen Split Sell plan snapshots; `tiers` (SavedPlanTier[])
 * goes into the JSONB column as-is.
 *
 * The `sell_unit` column has a CHECK (sell_unit in ('pct','coins','usd')). The
 * app's `SavedSellPlan.sellUnit` is the literal union `'pct' | 'coins' | 'usd'`
 * (and is optional — absent on plans saved before the sizing step), so present
 * values match the allowed set exactly and absence maps to NULL. No transform.
 */
import type { SavedSellPlan, SavedPlanTier } from '../../types/savedPlans';
import type { SavedSellPlanRow } from './rows';
import { makeAppendTable } from './client';

function toRow(p: SavedSellPlan, userId: string): SavedSellPlanRow {
  return {
    id: p.id,
    user_id: userId,
    coin_id: p.coinId,
    coin_name: p.coinName,
    coin_ticker: p.coinTicker,
    coin_image: p.coinImage ?? null,
    coins_held: p.coinsHeld,
    avg_buy_price: p.avgBuyPrice,
    tiers: p.tiers,
    tier_count: p.tierCount,
    expected_proceeds: p.expectedProceeds,
    currency: p.currency,
    plan_name: p.planName,
    saved_at: p.savedAt,
    sell_quantity: p.sellQuantity ?? null,
    sell_percent: p.sellPercent ?? null,
    sell_unit: p.sellUnit ?? null, // 'pct' | 'coins' | 'usd' | null — matches CHECK
    sell_value: p.sellValue ?? null,
  };
}

function fromRow(row: SavedSellPlanRow): SavedSellPlan {
  return {
    id: row.id,
    coinId: row.coin_id,
    coinName: row.coin_name,
    coinTicker: row.coin_ticker,
    ...(row.coin_image != null ? { coinImage: row.coin_image } : {}),
    coinsHeld: Number(row.coins_held),
    avgBuyPrice: Number(row.avg_buy_price),
    tiers: (row.tiers ?? []) as SavedPlanTier[],
    tierCount: Number(row.tier_count),
    expectedProceeds: Number(row.expected_proceeds),
    currency: row.currency,
    planName: row.plan_name,
    savedAt: row.saved_at,
    ...(row.sell_quantity != null ? { sellQuantity: Number(row.sell_quantity) } : {}),
    ...(row.sell_percent != null ? { sellPercent: Number(row.sell_percent) } : {}),
    ...(row.sell_unit != null ? { sellUnit: row.sell_unit } : {}),
    ...(row.sell_value != null ? { sellValue: Number(row.sell_value) } : {}),
  };
}

export const sellPlansTable = makeAppendTable<SavedSellPlan, SavedSellPlanRow>(
  'saved_sell_plans',
  toRow,
  fromRow
);
